import { memo, useCallback } from 'react';
import { useUserNotifications } from '@/hooks/queries/notification';
import {
  useInboxSession,
  useNextUnreadNotification,
  type InboxStatusFilter,
} from '@/components/notifications/InboxSessionContext';
import { NotificationItemV4 } from '@/components/notifications/NotificationItems';
import {
  NotificationsPagination,
  NotificationsErrorState,
  NotificationsEmptyState,
  NotificationsLoadingStateV3,
} from '@/components/notifications/NotificationsUI';
import { Button } from '@/components/ui/button';
import { ArrowRight, RefreshCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserNotificationRequest } from '@/types/notifications';

/**
 * Maps the inbox status filter to the `status` param sent to the API.
 * 'all' omits the param so the backend returns both read and unread notifications.
 */
function toStatusParam(statusFilter: InboxStatusFilter): UserNotificationRequest['status'] {
  return statusFilter === 'all' ? undefined : statusFilter;
}

/**
 * Unified notification list component with V4 design.
 * Displays the latest notifications in a single flat list with pagination.
 */
function NotificationsList({
  pageNo,
  pageSize,
  onPageChange,
}: {
  pageNo: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const { clearNextUnreadCursor, statusFilter, setStatusFilter } = useInboxSession();
  const { data, isPending, error, refetch, isFetching } = useUserNotifications({
    pageNo,
    pageSize,
    status: toStatusParam(statusFilter),
  });

  const handleManualPageChange = useCallback(
    (newPage: number) => {
      clearNextUnreadCursor();
      onPageChange(newPage);
    },
    [clearNextUnreadCursor, onPageChange],
  );

  const handleStatusFilterChange = useCallback(
    (value: InboxStatusFilter) => {
      clearNextUnreadCursor();
      setStatusFilter(value);
      onPageChange(1);
    },
    [clearNextUnreadCursor, setStatusFilter, onPageChange],
  );

  if (error) {
    return <NotificationsErrorState onRetry={() => refetch()} />;
  }

  const notifications = data?.data?.notifications ?? [];
  const totalPages = data?.data?.totalPages ?? 0;
  const totalItems = data?.data?.totalItems ?? 0;

  return (
    <div className="flex h-full flex-col">
      <NotificationsPagination
        disablePagination={isFetching || isPending}
        pageNo={pageNo}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handleManualPageChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
      />

      <div className="no-scrollbar flex-1 overflow-y-auto">
        {isPending ? (
          <NotificationsLoadingStateV3 />
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItemV4 key={notification.id} notification={notification} />
            ))}
            {notifications.length === 0 && <NotificationsEmptyState />}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Main notifications page component.
 */
function NotificationsPage({
  pageNo,
  pageSize,
  onPageChange,
}: {
  pageNo: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="h-screen px-4 pt-2">
      <NotificationsList
        pageNo={pageNo}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
}

/**
 * Header content for the notifications drawer — title, counts, Refresh, and Next Unread.
 */
NotificationsPage.HeaderContent = memo(
  ({
    pageNo,
    pageSize,
    onPageChange,
  }: {
    pageNo: number;
    pageSize: number;
    onPageChange: (page: number) => void;
  }) => {
    const { clearAcknowledgedNotifications, clearNextUnreadCursor, statusFilter } =
      useInboxSession();
    const { data, refetch, isPending, isFetching } = useUserNotifications({
      pageNo,
      pageSize,
      status: toStatusParam(statusFilter),
    });

    const totalItems = data?.data?.totalItems || 0;
    const unreadCount = data?.data?.unreadNotificationCount?.unreadCount || 0;
    const notifications = data?.data?.notifications ?? [];
    const totalPages = data?.data?.totalPages ?? 0;

    const { openNextUnread, hasMoreUnread } = useNextUnreadNotification({
      notifications,
      pageNo,
      totalPages,
      onPageChange,
    });

    const handleRefresh = useCallback(() => {
      clearAcknowledgedNotifications();
      clearNextUnreadCursor();
      void refetch();
    }, [clearAcknowledgedNotifications, clearNextUnreadCursor, refetch]);

    const isActionDisabled = isPending || isFetching;

    return (
      <div className="flex w-full items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Inbox</h2>
          <p className="text-sm text-gray-500">
            {totalItems} total
            {unreadCount > 0 && (
              <>
                {' • '}
                <span className="whitespace-nowrap font-semibold text-gray-700">
                  {unreadCount} unread
                </span>
              </>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            disabled={isActionDisabled}
            className="cursor-pointer border-gray-200 bg-white text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-100 hover:shadow-md"
            aria-label="Refresh inbox"
          >
            <RefreshCcw
              className={cn('h-4 w-4', isPending || isFetching ? 'animate-spin' : '')}
            />
          </Button>

          <Button
            onClick={openNextUnread}
            variant="outline"
            size="sm"
            disabled={isActionDisabled || !hasMoreUnread}
            className="cursor-pointer border-[#FDB302] bg-[#FDB302]/10 text-black transition-all duration-200 hover:border-[#e1a038] hover:bg-[#FDB302]/20 hover:text-black hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowRight className="mr-1.5 h-4 w-4 text-inherit" />
            Next Unread
          </Button>
        </div>
      </div>
    );
  },
);

export default NotificationsPage;
